import React, { useEffect, useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useDebounce } from 'hooks/useDebaunce';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryGetSingleWbs, useQueryGetWbs } from 'api/queries/getWbs.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { mapper } from 'components/common/GlobalFiltersInPage/components/WbsFilter/utils';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { Select } from 'components/DesignSystem/Select';
import { WbsOption } from 'components/DesignSystem/Select/components/WbsOption/WbsOption';

interface IWbsFilterProps {
    methods?: any;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    className?: string;
    versionId?: number | undefined;
    getSelectedValue?: (value: any) => void;
    value?: string;
    isSelected?: boolean;
}

export interface OptionValue {
    value: string;
    wbs_level: number;
}

export const WbsFilter = ({
    methods,
    name,
    filtersStatusRef,
    handleFilters,
    className,
    versionId,
    getSelectedValue,
    value,
    isSelected = true,
}: IWbsFilterProps) => {
    const [filter, setFilter] = React.useState('');
    const debouncedValue = useDebounce<string>(filter, 1000);

    const {
        data: wbs,
        isFetching,
        isSuccess,
    } = useQueryGetWbs({
        latestVersion: versionId,
        filter: debouncedValue,
    });

    const options = useMemo<ISelectOption<OptionValue>[]>(
        () => (wbs ? orderBy(wbs, [(item: any) => item.activity_id.toLowerCase()], ['asc']).map(mapper) : []),
        [wbs],
    );

    const { data: singleWbs } = useQueryGetSingleWbs({
        latestVersion: versionId,
        taskId: value, // make sure BE can perform search by task_version_hash_code
    });

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, wbs, singleWbs, filtersStatusRef, name, isSuccess]);

    const handleInputChangePrimary = (inputText, event) => {
        if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
            setFilter(inputText);
        }
        if (event.action === 'menu-close') {
            setFilter('');
        }
    };

    const handleChange = (option: ISelectOption<OptionValue> | null) => {
        methods?.setValue(name, option ? option.value.value : null);
        handleFilters && handleFilters();
        getSelectedValue && getSelectedValue(option);
    };

    const handleValue = (value) => {
        if (!value) {
            return null;
        }
        if (singleWbs && singleWbs.length > 0 && mapper(singleWbs[0]).value.value === value) {
            return mapper(singleWbs[0]);
        }
        return wbs?.map(mapper).find((item) => item.value.value === value) || null;
    };
    return isSelected ? (
        <div className={`${classes.selectWrapper} ${className ?? ''}`}>
            <div data-testid={'battlecards-filters-wbs'} className={classes.activityCodesSelectWrapper}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<OptionValue>>
                            {...field}
                            options={options}
                            id={'global-filter-component-wbs'}
                            onInputChange={handleInputChangePrimary}
                            isClearable={true}
                            isLoading={isFetching}
                            onChange={handleChange}
                            value={handleValue(value)}
                            placeholder={'Select WBS...'}
                            components={{
                                Option: WbsOption,
                            }}
                            label={FilterLabels.WBS_SEARCH}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    ) : null;
};
